import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { ProtectedRoute } from '@/app/routes/ProtectedRoute';
import { useAuthStore } from '@/app/store/auth.store';

import { AppShell } from '@/components/layout/AppShell';
import { MedicalLayout } from '@/pages/medical/MedicalLayout';
import { StandaloneLayout } from '@/components/layout/StandaloneLayout';
import { ComingSoonPage } from '@/pages/medical/screens/_shared/ComingSoonPage';
import { MedicalHomePage } from '@/pages/medical/screens/home/MedicalHomePage';
import { MedicalLoginPage } from '@/pages/medical/screens/login/MedicalLoginPage';
import { MedicalMyProfilePage } from '@/pages/medical/screens/my-profile/MedicalMyProfilePage';
import { MedicalNotificationsPage } from '@/pages/medical/screens/notifications/MedicalNotificationsPage';
import { MedicalSignupPage } from "@/pages/medical/screens/signup/MedicalSignupPage";
import { MedicalPatientsPage } from '@/pages/medical/screens/patients/MedicalPatientsPage';
import { MedicalTermsPoliciesPage } from '@/pages/medical/screens/terms-policies/MedicalTermsPoliciesPage';
import { TermsViewPage } from '@/pages/medical/screens/terms-policies/TermsViewPage';
import { ResetPasswordPage } from '@/pages/medical/screens/reset-password/ResetPasswordPage';

// DEV flag: when `true`, the medical app shell renders without ProtectedRoute,
// so screens can be iterated on without re-logging in each reload. Keep `false`
// for the login-first flow; flip to `true` only locally. Kept long-term for dev
// convenience (per project owner) — not a pre-ship cleanup TODO.
const TEMP_BYPASS_LOGIN = false;

// Terms viewer is a PUBLIC route (reachable from the logged-out login/signup
// footer AND from the authenticated Settings screen). Render the GNB shell only
// when authenticated — logged-out visitors get the bare page (like login/signup).
function TermsViewChrome() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? (
    <AppShell>
      <Outlet />
    </AppShell>
  ) : (
    <Outlet />
  );
}

export function MedicalRoutes() {
  return (
    <Routes>
      {/* Standalone — no GNB (login / signup / reset-password) */}
      <Route element={<StandaloneLayout />}>
        <Route path="/login" element={<MedicalLoginPage />} />
        <Route path="/signup" element={<MedicalSignupPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      {/* Public terms viewer — GNB shown only when authenticated (see TermsViewChrome) */}
      <Route element={<TermsViewChrome />}>
        <Route path="/terms-view" element={<TermsViewPage />} />
      </Route>

      {/* App shell — wrapped in ProtectedRoute (currently bypassed in dev) */}
      <Route element={TEMP_BYPASS_LOGIN ? <Outlet /> : <ProtectedRoute />}>
        <Route element={<MedicalLayout />}>
          <Route path="/home" element={<MedicalHomePage />} />
          <Route path="/patients" element={<MedicalPatientsPage />} />
          <Route path="/notifications" element={<MedicalNotificationsPage />} />
          <Route path="/my-profile" element={<MedicalMyProfilePage />} />
          <Route path="/settings" element={<MedicalTermsPoliciesPage />} />
          <Route
            path="/schedule"
            element={<ComingSoonPage screenKey="medical:screens.schedule" />}
          />
          <Route path="/" element={<Navigate to="/home" replace />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}
